const DB_NAME = 'point_sync';
const DB_VERSION = 1;

let dbPromise = null;

function entityKey(entity, id) {
  return `${String(entity || 'cari').toLowerCase()}:${id}`;
}

function openDb() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('meta')) db.createObjectStore('meta');
      if (!db.objectStoreNames.contains('index')) db.createObjectStore('index', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('bundles')) db.createObjectStore('bundles', { keyPath: 'key' });
      if (!db.objectStoreNames.contains('httpCache')) db.createObjectStore('httpCache');
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('indexedDB open failed'));
  });
  return dbPromise;
}

function txStore(storeName, mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    Promise.resolve(fn(store))
      .then((result) => {
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error || new Error('tx aborted'));
      })
      .catch(reject);
  }));
}

export async function idbSetMeta(key, value) {
  return txStore('meta', 'readwrite', (store) => store.put(value, key));
}

export async function idbGetMeta(key) {
  return txStore('meta', 'readonly', (store) => new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  }));
}

export async function idbReplaceIndex(entity, rows) {
  const ent = String(entity || 'cari').toLowerCase();
  return txStore('index', 'readwrite', async (store) => {
    const allReq = store.getAll();
    const existing = await new Promise((resolve, reject) => {
      allReq.onsuccess = () => resolve(allReq.result || []);
      allReq.onerror = () => reject(allReq.error);
    });
    for (const row of existing) {
      if (row?.entity === ent) store.delete(row.key);
    }
    for (const row of rows || []) {
      const id = Number(row.CAR_REF ?? row.entityId ?? row.id);
      if (!id) continue;
      store.put({ key: entityKey(ent, id), entity: ent, entityId: id, ...row });
    }
  });
}

export async function idbGetIndexAll(entity) {
  const ent = String(entity || 'cari').toLowerCase();
  return txStore('index', 'readonly', (store) => new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => {
      const rows = (req.result || []).filter((r) => r.entity === ent);
      resolve(rows);
    };
    req.onerror = () => reject(req.error);
  }));
}

export async function idbRemoveIndex(entity, entityId) {
  return txStore('index', 'readwrite', (store) => store.delete(entityKey(entity, entityId)));
}

export async function idbUpsertIndexRow(entity, row) {
  const id = Number(row?.CAR_REF ?? row?.entityId ?? row?.id ?? 0);
  if (!id) return;
  const ent = String(entity || 'cari').toLowerCase();
  return txStore('index', 'readwrite', (store) => store.put({
    key: entityKey(ent, id),
    entity: ent,
    entityId: id,
    ...row,
  }));
}

export async function idbTrimBundles(entity, maxCount = 120) {
  const ent = String(entity || 'cari').toLowerCase();
  return txStore('bundles', 'readwrite', async (store) => {
    const allReq = store.getAll();
    const rows = await new Promise((resolve, reject) => {
      allReq.onsuccess = () => resolve(allReq.result || []);
      allReq.onerror = () => reject(allReq.error);
    });
    const mine = rows.filter((r) => r?.entity === ent);
    if (mine.length <= maxCount) return;
    mine.sort((a, b) => (a.cachedAt || 0) - (b.cachedAt || 0));
    const excess = mine.length - maxCount;
    for (let i = 0; i < excess; i += 1) {
      store.delete(mine[i].key);
    }
  });
}

export async function idbPutBundle(entity, bundle) {
  const id = Number(bundle?.entityId ?? bundle?.carRef ?? 0);
  if (!id) return;
  return txStore('bundles', 'readwrite', (store) => store.put({
    key: entityKey(entity, id),
    entity: String(entity || 'cari').toLowerCase(),
    cachedAt: Date.now(),
    ...bundle,
  }));
}

export async function idbGetBundle(entity, entityId) {
  const id = Number(entityId);
  if (!id) return null;
  return txStore('bundles', 'readonly', (store) => new Promise((resolve, reject) => {
    const req = store.get(entityKey(entity, id));
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  }));
}

export async function idbDeleteBundle(entity, entityId) {
  return txStore('bundles', 'readwrite', (store) => store.delete(entityKey(entity, entityId)));
}

export async function idbSetHttpCache(key, data, ts = Date.now()) {
  return txStore('httpCache', 'readwrite', (store) => store.put({ data, ts }, key));
}

export async function idbGetHttpCache(key) {
  return txStore('httpCache', 'readonly', (store) => new Promise((resolve, reject) => {
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  }));
}

export async function idbDeleteHttpCache(key) {
  return txStore('httpCache', 'readwrite', (store) => store.delete(key));
}

export async function idbClearEntityCaches(entity, entityId) {
  const id = Number(entityId);
  if (!id) return;
  await idbDeleteBundle(entity, id);
  const suffix = `_${id}`;
  return txStore('httpCache', 'readwrite', (store) => new Promise((resolve, reject) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) { resolve(); return; }
      const key = String(cursor.key || '');
      if (key.includes(suffix)) cursor.delete();
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  }));
}

export async function idbPurgeOlderThanDate(entity, sinceIso) {
  const ent = String(entity || 'cari').toLowerCase();
  const cutoffMs = sinceIso ? new Date(sinceIso).getTime() : 0;
  if (!cutoffMs) return;

  // Yalnızca index: CAR_GIR_TAR penceresi dışı satırları düşür.
  // Arama/açılışla cache'lenen eski cari bundle'ları silinmez (LRU trim eder).
  await txStore('index', 'readwrite', (store) => new Promise((resolve, reject) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) { resolve(); return; }
      const row = cursor.value;
      if (row?.entity !== ent) { cursor.continue(); return; }
      const girMs = row?.CAR_GIR_TAR ? new Date(row.CAR_GIR_TAR).getTime() : 0;
      if (girMs && girMs < cutoffMs) {
        cursor.delete();
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  }));
}

export async function idbPurgeOlderThanMonths(entity, months = 2) {
  const ent = String(entity || 'cari').toLowerCase();
  const cutoff = new Date();
  cutoff.setMonth(cutoff.getMonth() - months);
  const cutoffMs = cutoff.getTime();

  await txStore('index', 'readwrite', (store) => new Promise((resolve, reject) => {
    const req = store.openCursor();
    req.onsuccess = () => {
      const cursor = req.result;
      if (!cursor) { resolve(); return; }
      const row = cursor.value;
      if (row?.entity !== ent) { cursor.continue(); return; }
      const gir = row?.CAR_GIR_TAR ? new Date(row.CAR_GIR_TAR).getTime() : 0;
      if (gir && gir < cutoffMs) {
        cursor.delete();
      }
      cursor.continue();
    };
    req.onerror = () => reject(req.error);
  }));
}

/** Eski DB adından göç (point_cari_sync) */
export async function migrateLegacyCariDbIfNeeded() {
  if (typeof indexedDB === 'undefined') return;
  const legacy = indexedDB.open('point_cari_sync');
  legacy.onsuccess = () => { try { indexedDB.deleteDatabase('point_cari_sync'); } catch { /* ignore */ } };
}
